import type { FeatureTranslation } from "../types";

export const kesiswaanEn: Record<string, FeatureTranslation> = {
  "prestasi-pelanggaran": {
    label: "Achievements & Conduct",
    navDescription: "Character development records",
    summary:
      "Record student achievements, conduct breaches, and pastoral progress, open to parents.",
    detail: {
      title: "Character development with *a record*, not just recollection",
      lead: "Achievements, conduct breaches, and pastoral progress recorded and visible to parents",
      intro:
        "Conduct notes have traditionally lived in a form teacher's notebook, and achievements were only remembered when a competition came around. Once both are captured in the same system, pastoral work stops depending on who happens to remember — and parents see how their child is doing without being called in.",
      highlights: [
        "Achievements and conduct breaches recorded with dates and detail",
        "Pastoral progress tracked alongside them",
        "Open to parents through the app",
      ],
      benefits: [
        {
          title: "Not dependent on one teacher",
          body: "Records live in the system, not in a form teacher's private notebook. When a form teacher changes, a child's pastoral history stays intact.",
        },
        {
          title: "Achievements do not evaporate",
          body: "Student accomplishments are documented across the year, giving a basis when the school writes a reference or selects a representative for a competition.",
        },
        {
          title: "Parents join the effort",
          body: "Because records are open in the app, parents know earlier and can help address things — rather than being told only once a problem has grown.",
        },
        {
          title: "Progress you can see",
          body: "The progress module shows how pastoral work is developing, so improvement in behaviour reads as a process rather than a single incident.",
        },
      ],
      screensTitle: "Three records that complete each other",
      screensLead:
        "Parents open all three from the Student Affairs menu in the CARDS app.",
      screens: [
        {
          alt: "List of student achievements in the CARDS app",
          caption: "Achievements",
          note: "A student's accomplishments across the year",
        },
        {
          alt: "Student conduct records in the CARDS app",
          caption: "Conduct",
          note: "Records with their dates and detail",
        },
        {
          alt: "Pastoral development progress in the CARDS app",
          caption: "Progress",
          note: "How pastoral work is developing",
        },
      ],
      faq: [
        {
          q: "Are all conduct records immediately visible to parents?",
          a: "How openly records are shared follows your school's policy. Either way, they are stored in the system so follow-up does not rest on one teacher's memory.",
        },
        {
          q: "Is there a points system?",
          a: "The weighting of achievements and conduct breaches, and what follows from them, is configured to match your school's pastoral rules during implementation.",
        },
        {
          q: "Who is allowed to add a record?",
          a: "Permission to add records is set through user settings, so only the roles you authorise — a form teacher or counsellor, for instance — can fill them in.",
        },
      ],
      metaTitle: "Student Achievement & Conduct Records",
      metaDescription:
        "Record student achievements, conduct breaches, and pastoral progress in one system that stays open to parents through the CARDS app.",
      keywords: [
        "student conduct records",
        "school behaviour points system",
        "digital student achievements",
        "student character development",
        "school student affairs software",
      ],
    },
  },

  "perizinan-kunjungan": {
    label: "Leave & Visits",
    navDescription: "Leave requests & guardian visits",
    summary:
      "Student leave requests and guardian visits, complete with their approvals.",
    detail: {
      title: "Leave and visits that are *recorded, not sent by text*",
      lead: "Student leave requests and guardian visits get a formal path and a history",
      intro:
        "Leave requested by message to a form teacher is easily lost among hundreds of other chats, and guardian visits are often only written in a visitors' book nobody opens again. Both are moved onto a formal path so the school knows exactly who left, when, and with whose permission.",
      highlights: [
        "Leave requested through the app, not a private message",
        "Guardian visits recorded with their timestamps",
        "A history you can trace when you need it",
      ],
      benefits: [
        {
          title: "Requests do not drown in chat",
          body: "A request arrives as data, not a message a form teacher has to dig back through other conversations to find.",
        },
        {
          title: "The school knows who has left",
          body: "Every departure is recorded with its timestamp — important for boarding schools and for any school that controls its gate.",
        },
        {
          title: "Visits are documented",
          body: "Parent and guardian visits are recorded systematically, giving the school a visit history it can look back through.",
        },
        {
          title: "Joined to the rest of the record",
          body: "Leave and visits sit in the same module as other pastoral records, so the picture of a student is not scattered across places.",
        },
      ],
      screensTitle: "Two flows in the Student Affairs menu",
      screensLead:
        "Parents submit and review both from the same app.",
      screens: [
        {
          alt: "Student leave requests and history in the CARDS app",
          caption: "Leave",
          note: "Requests along with their history",
        },
        {
          alt: "Guardian visit records in the CARDS app",
          caption: "Visits",
          note: "A history of guardian visits to the school",
        },
      ],
      faq: [
        {
          q: "Does leave need approval before it takes effect?",
          a: "The approval flow follows your school's policy. Either way, every request is recorded so its status is clear and traceable.",
        },
        {
          q: "Is this module suited to boarding schools?",
          a: "Very much so. Leave from the dormitory and guardian visits are daily realities for boarding schools, and this module handles both.",
        },
        {
          q: "How is this different from the Visitors' Book?",
          a: "Visits record a guardian arriving in connection with a particular student, while the Visitors' Book records school visitors generally, in the Announcements module.",
        },
      ],
      metaTitle: "Online Student Leave & Guardian Visits",
      metaDescription:
        "Student leave requests and guardian visits handled through a formal in-app path, with a traceable history for schools and boarding schools.",
      keywords: [
        "student leave request software",
        "boarding school leave system",
        "guardian visit records",
        "school permission app",
        "digital student leave",
      ],
    },
  },

  "kesehatan-perundungan": {
    label: "Health & Bullying",
    navDescription: "Health records & reporting",
    summary:
      "Record student health conditions and provide a bullying reporting channel that gets acted on.",
    detail: {
      title: "Two things a school *cannot afford to miss*",
      lead: "Student health records and a bullying reporting channel in one pastoral module",
      intro:
        "Health histories are often known only to whoever is staffing the sick bay, and bullying frequently comes to light only after it has gone on too long. Both are placed on the record so the school has something to act on — and parents have a way to speak up.",
      highlights: [
        "Records of student health conditions and treatment given",
        "A bullying reporting channel that is recorded",
        "Visible to parents through the Student Affairs menu",
      ],
      benefits: [
        {
          title: "A health history that stays",
          body: "Specific conditions, allergies, and treatment previously given are held in the system — not just known to whoever happens to be on duty.",
        },
        {
          title: "Bullying gets a formal route",
          body: "A report arrives as a traceable record, rather than a spoken complaint that easily stalls partway.",
        },
        {
          title: "Parents can raise it",
          body: "Because the module is open in the parent app, a concern can be raised early without waiting for a parents' evening.",
        },
        {
          title: "A basis for acting",
          body: "An orderly record gives the school objective footing when deciding how to respond, rather than relying on conflicting accounts.",
        },
      ],
      screensTitle: "Reporting from the parent's side",
      screensLead:
        "Placed in the Student Affairs menu, alongside achievements and leave.",
      screens: [
        {
          alt: "Bullying reporting in the CARDS parent app",
          caption: "Bullying",
          note: "A reporting channel that is recorded and traceable",
        },
      ],
      faq: [
        {
          q: "Can a bullying report be made anonymously?",
          a: "Confidentiality for the reporter follows your school's child protection policy, and is configured during implementation.",
        },
        {
          q: "Who can open a student's health record?",
          a: "Access is set through user settings, so only the roles you authorise — sick bay staff and counsellors, for instance — can open them.",
        },
        {
          q: "Is the health record linked to attendance?",
          a: "Both sit in the same system, so an absence through illness can be read alongside the student's health record.",
        },
      ],
      metaTitle: "Student Health Records & Bullying Reporting",
      metaDescription:
        "Record student health conditions and treatment, and provide a bullying reporting channel that is recorded and can be acted on by the school.",
      keywords: [
        "student health records",
        "school clinic software",
        "school bullying reporting",
        "anti bullying school system",
        "student wellbeing app",
      ],
    },
  },

  tahfidz: {
    label: "Qur'an Memorisation",
    navDescription: "Recitation progress by surah & juz",
    summary:
      "Record students' Qur'an memorisation by surah and juz, with progress you can follow.",
    detail: {
      title: "Memorisation *recorded by surah and juz*",
      lead: "A student's Qur'an memorisation progress kept in order and followed by parents from home",
      intro:
        "A paper recitation book is easily damaged, left behind, or lost — and when that happens, months of progress go with it. Recorded digitally, every recitation is kept, so teachers know exactly how far a student has come and parents can follow along without waiting for a visit.",
      highlights: [
        "Recitations recorded by surah and by juz",
        "Memorisation progress tracked over time",
        "Parents follow the progress from the app",
      ],
      benefits: [
        {
          title: "No progress is lost",
          body: "The recitation history is held in the system, so a damaged or forgotten book no longer means losing months of records.",
        },
        {
          title: "Teachers know where to resume",
          body: "Each student's progress reads clearly, so a session can begin at exactly the right point without guesswork.",
        },
        {
          title: "Parents can encourage",
          body: "Memorisation progress is open in the app, letting parents keep encouraging their child even while they are boarding.",
        },
        {
          title: "Joined to the rest of the record",
          body: "Memorisation sits in the same pastoral module as activity attendance and leave, keeping the picture of a student whole.",
        },
      ],
      screensTitle: "Memorisation progress in the parent app",
      screensLead:
        "Opened from the Student Affairs menu, alongside a student's other records.",
      screens: [
        {
          alt: "Qur'an memorisation progress in the CARDS app",
          caption: "Memorisation",
          note: "Recitation history by surah and juz",
        },
      ],
      faq: [
        {
          q: "Can the assessment method be adapted?",
          a: "The components used to assess a recitation are configured during implementation to match the memorisation method your school follows.",
        },
        {
          q: "Can one student be heard by several teachers?",
          a: "Recording follows the school's user settings, so several supervising teachers can be granted access according to how you divide the work.",
        },
        {
          q: "Is this module only for boarding schools?",
          a: "It is most used by Islamic boarding and integrated Islamic schools, but it is available to any institution running a memorisation programme.",
        },
      ],
      metaTitle: "Qur'an Memorisation Tracking Software",
      metaDescription:
        "Record students' Qur'an memorisation by surah and juz. Progress is tracked by teachers and can be followed by parents in the CARDS app.",
      keywords: [
        "quran memorisation software",
        "tahfidz tracking app",
        "hifz progress tracker",
        "islamic boarding school software",
        "digital quran recitation records",
      ],
    },
  },

  "ppdb-online": {
    label: "Online Admissions",
    navDescription: "Intakes, pathways, forms, fees",
    summary:
      "Online student admissions — intakes, pathways, forms, document uploads, and registration fees.",
    detail: {
      title: "Admissions that run *without a queue at the desk*",
      lead: "A registration page, forms, intake schedules, and applicant data in one system",
      intro:
        "Admissions season usually means stacks of paper forms and an overwhelmed committee. With online admissions, applicants register from home through the school's own admissions page, while the committee watches applications arrive on a dashboard rather than in a folder.",
      highlights: [
        "A school admissions page with configurable templates",
        "A standard application form you can adapt",
        "Intake schedules and applicant data tracked throughout",
      ],
      benefits: [
        {
          title: "Your own admissions page",
          body: "The school configures its admissions landing page, including the application flow and the active template, so applicants know exactly where to begin.",
        },
        {
          title: "A form you can adapt",
          body: "Standard form settings let the school tailor the fields to its needs, rather than being forced into a single fixed format.",
        },
        {
          title: "Intakes on a schedule",
          body: "Schedule settings determine when each intake opens and closes, so the committee does not have to remember and announce it manually.",
        },
        {
          title: "Applicants tracked from the start",
          body: "Applicant data reaches the dashboard the moment a form is submitted, giving the committee a live picture of how admissions are going.",
        },
      ],
      screensTitle: "What the admissions committee works with",
      screensLead:
        "Set up the page and the schedule at the start, then simply watch who applies.",
      screens: [
        {
          alt: "Admissions landing page settings with active template",
          caption: "Landing Page Settings",
          note: "Application flow and admissions page template",
        },
        {
          alt: "Intake schedule settings for student admissions",
          caption: "Schedule Settings",
          note: "Opening and closing for each intake",
        },
        {
          alt: "Student admissions applicant data in the dashboard",
          caption: "Applicant Data",
          note: "Applications received and their statuses",
        },
      ],
      faq: [
        {
          q: "Can applicants upload supporting documents?",
          a: "Yes. The application form covers data entry alongside document uploads, so the committee does not need to receive physical photocopies.",
        },
        {
          q: "What about the registration fee?",
          a: "Registration fees are paid through the CARDS payment channel and verified automatically, so an applicant's payment status is immediately visible to the committee.",
        },
        {
          q: "Can accepted applicants become students directly?",
          a: "Yes. Accepted applicant data can be carried forward into student records, so nothing has to be retyped at the start of the academic year.",
        },
      ],
      metaTitle: "Online School Admissions Software",
      metaDescription:
        "Online student admissions: your own admissions page, an adaptable form, intake schedules, document uploads, and automatically verified fee payments.",
      keywords: [
        "online school admissions",
        "student enrolment software",
        "school application system",
        "digital admissions platform",
        "new student registration online",
      ],
    },
  },

  "data-anggota": {
    label: "Members & Alumni",
    navDescription: "Students, teachers, staff, alumni",
    summary:
      "A database of students, teachers, staff, and alumni, including re-enrolment and self-service updates.",
    detail: {
      title: "One database that *every module reads*",
      lead: "Students, teachers, staff, and alumni in one source — including re-enrolment and data updates",
      intro:
        "Attendance, billing, cards, and report cards all rest on the same member data. So students, teachers, staff, and alumni are held in one place, with statuses that change as they move through their journey — from applicant, to active student, to alumnus.",
      highlights: [
        "Students, teachers, staff, and alumni in one system",
        "Statuses that follow the journey, including reverting to active",
        "Re-enrolment and self-service updates by parents",
      ],
      benefits: [
        {
          title: "No conflicting versions of the data",
          body: "Because every module reads the same source, a name can no longer differ between the attendance register, the billing list, and the report card.",
        },
        {
          title: "Alumni stay connected",
          body: "Students who graduate move to alumnus status rather than being deleted. Their data remains searchable and filterable whenever the school needs it.",
        },
        {
          title: "Statuses can be corrected",
          body: "If a mistake is made, an alumnus can be returned to active student status — the change is not a one-way door.",
        },
        {
          title: "Updated by those who know",
          body: "Self-service updates move address and contact changes to parents, easing the load on the office while keeping the data accurate.",
        },
      ],
      screensTitle: "Managing alumni from the dashboard",
      screensLead:
        "Search, filters, and bulk selection — built for data that grows every year.",
      screens: [
        {
          alt: "Student alumni list in the Cards School dashboard",
          caption: "Student Alumni",
          note: "Search, filters, and alumni status changes",
        },
      ],
      faq: [
        {
          q: "What happens to student data after graduation?",
          a: "The status changes to alumnus and the data is retained, so it remains searchable and filterable — it is not removed from the system.",
        },
        {
          q: "Can parents update their own details?",
          a: "Yes. Data update settings are available so changes such as address and contact details can be made directly, without going through the office.",
        },
        {
          q: "How does re-enrolment for existing students work?",
          a: "Re-enrolment for existing students is a separate process within the members module, so annual re-registration no longer relies on paper forms.",
        },
      ],
      metaTitle: "Student, Staff & Alumni Database for Schools",
      metaDescription:
        "One database for students, teachers, staff, and alumni. Includes re-enrolment for existing students and self-service data updates by parents.",
      keywords: [
        "school student database",
        "school alumni management",
        "student information system",
        "online school re-enrolment",
        "student data management",
      ],
    },
  },

  pengumuman: {
    label: "Announcements & WhatsApp",
    navDescription: "Broadcasts & visitors' book",
    summary:
      "Send announcements to parents through the app and WhatsApp, plus a school visitors' book.",
    detail: {
      title: "Announcements that *arrive*, instead of sinking into a group chat",
      lead: "Broadcast to the parent app and WhatsApp, plus a school visitors' book",
      intro:
        "Announcements in a group chat are quickly buried under other messages, and paper circulars often never reach parents at all. The announcements module sends official information through the channels parents actually open — the CARDS app and WhatsApp — giving the school one route it can rely on.",
      highlights: [
        "Announcements appear on the parent app home screen",
        "WhatsApp delivery for anything urgent",
        "A visitors' book for recording arrivals at school",
      ],
      benefits: [
        {
          title: "On the first screen",
          body: "Announcements appear on the app home screen rather than buried in conversation. Parents see them while opening the app for something else.",
        },
        {
          title: "WhatsApp for the urgent",
          body: "For information that cannot wait, WhatsApp delivery reaches parents on the channel they open most often.",
        },
        {
          title: "One official route",
          body: "Because announcements come from the school through the system, there is no longer confusion about which information is official and which is hearsay.",
        },
        {
          title: "School visitors recorded",
          body: "A digital visitors' book records arrivals at the school, replacing the physical book on the front desk that is rarely looked at again.",
        },
      ],
      screensTitle: "Announcements on the parent home screen",
      screensLead:
        "Shown as image cards, alongside school information and offers.",
      screens: [
        {
          alt: "School announcement cards on the CARDS app home screen",
          caption: "Announcements",
          note: "Published to the parent app home screen",
        },
      ],
      faq: [
        {
          q: "Can announcements be targeted at particular classes?",
          a: "Recipient targeting follows your school's needs and is configured during implementation, so an announcement need not always go to the whole school.",
        },
        {
          q: "Does the school know who has read an announcement?",
          a: "Because announcements are published through the system rather than as loose messages, their delivery can be traced. Our team can walk through the scope with you.",
        },
        {
          q: "What is the Visitors' Book for?",
          a: "The visitors' book records arrivals at the school generally, separate from the Visits module which records guardian visits about a particular student.",
        },
      ],
      metaTitle: "School Announcements via App & WhatsApp",
      metaDescription:
        "Send official school announcements to the parent app home screen and WhatsApp, with a digital visitors' book for recording arrivals at school.",
      keywords: [
        "school announcement software",
        "school whatsapp broadcast",
        "parent communication school",
        "digital school visitors book",
        "school notification app",
      ],
    },
  },
  "multi-lembaga": {
    label: "Multi-Institution",
    navDescription: "Many units under one foundation account",
    summary:
      "Run several schools or boarding school units from one account — each institution's data separate, the foundation's view unified.",
    detail: {
      title: "One account for *every unit in the foundation*",
      lead: "Primary, secondary, high school, and the boarding house — each runs on its own data while the foundation watches from a single door",
      intro:
        "A foundation with many units usually means many systems, many logins, and reports that never match. In CARDS, every institution stands on its own data, academic year, and finances — while the foundation's administrators manage them all from one and the same account.",
      highlights: [
        "Several institutions managed from a single account",
        "Academic years, data, and finances separated per institution",
        "A balance per institution, with a recorded head-office ↔ unit flow",
      ],
      benefits: [
        {
          title: "One login, every unit",
          body: "Foundation administrators stop hopping between systems: every unit under their care is managed from the same account.",
        },
        {
          title: "No unit's data bleeds into another's",
          body: "Each institution keeps its own active academic year, member data, and financial records — the units' autonomy stays intact.",
        },
        {
          title: "Head office and unit finances connected",
          body: "Balances are held per institution; funds can move between head office and units, be topped up by virtual account, or be withdrawn to the institution's bank account — all with a trail.",
        },
        {
          title: "Reports that finally line up",
          body: "With every unit on the same system, the foundation compares institutions on identically shaped data, instead of stitching together mismatched reports.",
        },
      ],
      screensTitle: "How one account runs many institutions",
      screensLead:
        "From registering the units to the foundation's cross-institution view.",
      steps: [
        {
          title: "Register the units",
          body: "Every school or boarding house under the foundation is registered as an institution with its own profile.",
        },
        {
          title: "Each unit runs on its own",
          body: "Each institution's admin manages its academic year, members, attendance, and billing without touching the other units.",
        },
        {
          title: "Funds move on the record",
          body: "Each institution's balance holds its own funds; transfers between head office and units are recorded with their history.",
        },
        {
          title: "The foundation watches from one door",
          body: "Head-office administrators open each unit's condition — finances through operations — without requesting manual reports.",
        },
      ],
      faq: [
        {
          q: "Can each unit keep different policies?",
          a: "Yes. Settings such as the academic year, fee types, and attendance schedules apply per institution, so each unit follows its own way of working.",
        },
        {
          q: "How is access split between the foundation and unit admins?",
          a: "Through user settings: a unit admin manages their own institution, while the foundation account reaches every unit beneath it.",
        },
        {
          q: "Can funds be moved between units?",
          a: "Fund movements run through the head-office balance: a unit withdraws to head office, or head office tops up a unit — and every movement is recorded in the transaction history.",
        },
      ],
      metaTitle: "Multi-Institution Management for Foundations",
      metaDescription:
        "Run many schools and boarding school units from one foundation account. Data and finances stay separate per institution, with a unified head-office view.",
      keywords: [
        "multi school management",
        "education foundation dashboard",
        "multi campus school software",
        "foundation school system",
        "multi branch school management",
      ],
    },
  },
};
